import React, {PureComponent, Fragment} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Button from '../button/Button';
import Icon from '../icon/Icon';
import Message from '../message/Message';
import {
    CustomPropShapes,
    buttonTypes,
    priority as buttonPriorities,
    smartButtonSubTypes,
    sizes,
    svgs,
    themes
} from '../../constants';
import {formatNumber} from '../../utils/GeneralUtils';

import styles from './SmartButton.pcss';

class SmartButton extends PureComponent {
    static propTypes = {
        disabled: PropTypes.bool,
        className: PropTypes.string,
        shouldForceHover: PropTypes.bool,
        shouldHideTheme: PropTypes.bool,
        id: PropTypes.string,
        isLarge: PropTypes.bool,
        indeterminate: PropTypes.bool,
        messageCode: PropTypes.string,
        onCancel: PropTypes.func,
        onPlay: PropTypes.func,
        onPause: PropTypes.func,
        onSettings: PropTypes.func,
        isSettingsOpen: PropTypes.bool,
        onClick: PropTypes.func,
        progress: PropTypes.number,
        theme: CustomPropShapes.oneOfConstant(themes),
        type: CustomPropShapes.oneOfConstant(buttonTypes),
        priority: CustomPropShapes.oneOfConstant(buttonPriorities),
        disableHoverText: PropTypes.bool,
        children: PropTypes.node
    };

    static defaultProps = {
        theme: themes.LIGHT,
        type: buttonTypes.BUTTON,
        priority: buttonPriorities.PRIMARY,
        isSettingsOpen: false,
        disableHoverText: false
    };

    state = {
        buttonText: undefined
    };

    renderMainButton = isInProgress => {
        const {
            disabled,
            id,
            messageCode,
            onClick,
            progress,
            theme,
            type,
            priority,
            shouldHideTheme,
            onCancel,
            onSettings,
            isSettingsOpen,
            onPlay,
            onPause,
            children
        } = this.props;
        const {buttonText} = this.state;
        const code =
            (isSettingsOpen && 'diesel.common.smart_button.settings') || buttonText || messageCode;
        const hasSubActions = onCancel || onSettings || onPlay || onPause;
        const buttonClasses = cx(styles.mainButton, {
            [styles.alignLeft]: isInProgress || hasSubActions,
            [styles.noEvent]: isSettingsOpen || !onClick
        });

        const renderMessage = () => (
            <Message
                className={styles.topContent}
                code={code}
                args={[formatNumber(Math.round(progress), 0)]}
            />
        );

        const renderChildren = () => {
            return buttonText ? renderMessage() : children;
        };

        return (
            <Button
                shouldForceHover={this.checkShouldForceHover()}
                shouldHideTheme={shouldHideTheme}
                className={buttonClasses}
                id={id && `${id}-main`}
                onClick={onClick}
                theme={theme}
                type={type}
                priority={priority}
                disabled={disabled}
                hasMinWidth={false}>
                {children ? renderChildren() : renderMessage()}
            </Button>
        );
    };

    toggleButtonText = buttonText => {
        this.setState({buttonText});
    };

    resetButtonText = () => {
        this.setState({buttonText: undefined});
    };

    checkShouldForceHover = () => {
        const {disabled, isSettingsOpen, onClick, shouldForceHover} = this.props;
        const noForceHover = isSettingsOpen || !onClick || disabled;
        if (noForceHover) return false;
        return shouldForceHover;
    };

    renderSubButton = (id, icon, onClick, buttonText) => {
        const {disabled, disableHoverText, shouldHideTheme, theme} = this.props;

        const onMouseEnter = () => {
            if (!disableHoverText) this.toggleButtonText(buttonText);
        };

        const onMouseLeave = () => {
            if (!disableHoverText) this.resetButtonText();
        };

        return (
            <Button
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                className={styles.iconButton}
                shouldHideTheme={shouldHideTheme}
                id={id}
                onClick={onClick}
                theme={theme}
                disabled={disabled}
                hasMinWidth={false}>
                <Icon
                    className={cx(styles.topContent, styles.icon)}
                    icon={icon}
                    size={sizes.XXXSMALL}
                />
            </Button>
        );
    };

    renderSubButtonsGroup = () => {
        const {id, isSettingsOpen, onCancel, onPlay, onPause, onSettings} = this.props;

        return (
            <Fragment>
                {onCancel &&
                    this.renderSubButton(
                        id && `${id}-cancel`,
                        svgs.CLOSE,
                        onCancel,
                        smartButtonSubTypes.onCancel
                    )}
                {onPlay &&
                    this.renderSubButton(
                        id && `${id}-play`,
                        svgs.PLAY,
                        onPlay,
                        smartButtonSubTypes.onPlay
                    )}
                {onPause &&
                    this.renderSubButton(
                        id && `${id}-pause`,
                        svgs.PAUSE,
                        onPause,
                        smartButtonSubTypes.onPause
                    )}
                {onSettings &&
                    !isSettingsOpen &&
                    this.renderSubButton(
                        id && `${id}-settings`,
                        svgs.SETTINGS,
                        onSettings,
                        smartButtonSubTypes.onSettings
                    )}
                {onSettings &&
                    isSettingsOpen &&
                    this.renderSubButton(
                        id && `${id}-settings`,
                        svgs.CLOSE,
                        null,
                        smartButtonSubTypes.onSettings
                    )}
            </Fragment>
        );
    };

    componentDidUpdate(prevProps) {
        const {messageCode} = this.props;
        if (messageCode !== prevProps.messageCode) {
            this.setState({buttonText: undefined}); //eslint-disable-line react/no-did-update-set-state
        }
    }

    render() {
        const {className, id, isLarge, progress, indeterminate} = this.props;

        const isInProgress = progress || indeterminate;

        const wrapperClasses = cx(className, styles.wrapper, {[styles.large]: isLarge});

        const progressClasses = cx(styles.progress, {[styles.indeterminate]: indeterminate});

        return (
            <div className={wrapperClasses} id={id}>
                {this.renderMainButton(isInProgress)}
                {this.renderSubButtonsGroup()}
                {isInProgress && (
                    <div className={styles.progressWrapper}>
                        <div
                            className={progressClasses}
                            style={{width: `${Math.round(progress)}%`}}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default SmartButton;
